// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — the turn, as a state machine over the legal ply list.
//
// THE ONE THING THAT MUST NEVER HAPPEN is a tap that half-commits something illegal, so no ply is
// ever assembled and then checked: the machine FILTERS the legal plies as taps arrive, and the
// points it offers are, by construction, exactly the points that lead to a legal ply. An illegal
// combination is unreachable rather than rejected.
//
// The game has three phases and they take a different number of taps each — one to set a torc down,
// two to ride one, two to claim a line — so instead of three machines this is ONE machine over the
// ply's two fields, plus a small translation layer that knows which board point each field means in
// each phase. That translation is the only phase-specific code in the file.
//
// EXACTLY ONE STAGE IS LIVE AT A TIME, which is what lets the two target styles be told apart on a
// board where several things are already bright: ride destinations (a violet ring on an empty point)
// and the torcs you may give up (a white halo on your own annulus) are never on screen together.
// tests/contrast.test.ts leans on that, so it is a rule, not a coincidence.

import {
  claimAnchor,
  claimAxis,
  isRing,
  ownerOf,
  packClaim,
  plyA,
  plyB,
  runsFor,
  type State,
} from './rules';

export type Stage = 'first' | 'second';

export interface TurnUi {
  /** The ply's first field once chosen — a point for a place or a ride, a run key for a claim. */
  a: number | null;
}

export const freshTurn = (): TurnUi => ({ a: null });

export const stageOf = (ui: TurnUi): Stage => (ui.a === null ? 'first' : 'second');

/** Which board point does this ply's FIRST field sit on? */
function firstCell(s: State, ply: number): number {
  if (s.phase === 'claim') return claimAnchor(ply);
  return plyA(ply);
}

/** The plies still reachable from what has been tapped so far. */
export function candidates(legal: number[], ui: TurnUi): number[] {
  if (ui.a === null) return legal;
  return legal.filter((p) => plyA(p) === ui.a);
}

/**
 * Every point that can START a turn.
 *
 * For a claim that means every stud in a claimable line, not just its anchor — a player asked to
 * "tap the line" will tap the middle of it, and requiring the exact end point would read as the
 * board ignoring them.
 */
export function pickable(legal: number[], s: State): Set<number> {
  const out = new Set<number>();
  if (s.phase === 'claim') {
    for (const run of runsFor(s, s.turn)) for (const c of run.cells) out.add(c);
    return out;
  }
  for (const p of legal) out.add(firstCell(s, p));
  return out;
}

/** The points the SECOND stage is asking for. Empty in `first` — the prompt carries that one. */
export function targets(legal: number[], ui: TurnUi): Set<number> {
  const out = new Set<number>();
  if (ui.a === null) return out;
  for (const p of candidates(legal, ui)) out.add(plyB(p));
  return out;
}

/** The studs of the line currently chosen for a claim, so the board can show what is coming off. */
export function chosenRun(ui: TurnUi, s: State): number[] {
  if (ui.a === null || s.phase !== 'claim') return [];
  const anchor = Math.floor(ui.a / 3);
  const axis = ui.a % 3;
  const run = runsFor(s, s.turn).find((r) => r.anchor === anchor && r.axis === axis);
  return run ? run.cells.slice() : [];
}

/** Every claimable line, for the board to outline before one is chosen. */
export function offeredRuns(s: State): number[][] {
  if (s.phase !== 'claim') return [];
  return runsFor(s, s.turn).map((r) => r.cells);
}

export type TapResult =
  | { kind: 'advance' }
  | { kind: 'cancel' }
  | { kind: 'commit'; ply: number }
  | { kind: 'reject' };

/**
 * Apply a tap. The caller owns the repaint; this owns the truth.
 *
 * A one-tap phase commits on the first tap. A two-tap phase commits on the second, and tapping the
 * first choice again — or a different valid first choice — re-picks rather than making the player
 * find Undo. On a phone that difference is the whole feel of the thing.
 */
export function tap(legal: number[], ui: TurnUi, cell: number, s: State): TapResult {
  if (!legal.length) return { kind: 'reject' };

  if (s.phase === 'place') {
    const ply = legal.find((p) => plyA(p) === cell);
    return ply === undefined ? { kind: 'reject' } : { kind: 'commit', ply };
  }

  if (s.phase === 'claim') {
    // Tapping any stud of a claimable line chooses that line. A stud shared by two lines resolves to
    // the first — tapping a stud unique to the other one picks that instead, which is the natural
    // thing to try and needs no extra affordance.
    const runs = runsFor(s, s.turn);
    const hit = runs.find((r) => r.cells.includes(cell));
    if (hit) {
      const key = hit.anchor * 3 + hit.axis;
      if (ui.a === key) {
        ui.a = null;
        return { kind: 'cancel' };
      }
      ui.a = key;
      return { kind: 'advance' };
    }
    if (ui.a === null) return { kind: 'reject' };
    const ply = candidates(legal, ui).find((p) => plyB(p) === cell);
    if (ply === undefined) return { kind: 'reject' };
    return { kind: 'commit', ply };
  }

  // ── the ride ──
  const mine = isRing(s.cell[cell]) && ownerOf(s.cell[cell]) === s.turn;
  if (mine) {
    if (ui.a === cell) {
      ui.a = null;
      return { kind: 'cancel' };
    }
    // Only offer a torc that actually has somewhere to go.
    if (!legal.some((p) => plyA(p) === cell)) return { kind: 'reject' };
    ui.a = cell;
    return { kind: 'advance' };
  }
  if (ui.a === null) return { kind: 'reject' };
  const ply = candidates(legal, ui).find((p) => plyB(p) === cell);
  if (ply === undefined) return { kind: 'reject' };
  return { kind: 'commit', ply };
}

/** One step back. Returns false when there was nothing to undo. */
export function undo(ui: TurnUi): boolean {
  if (ui.a === null) return false;
  ui.a = null;
  return true;
}

export function promptFor(s: State, ui: TurnUi, yours: boolean, runLen: number): string {
  if (!yours) return '';
  if (s.phase === 'place') return 'Set a torc down on any empty point.';
  if (s.phase === 'claim') {
    return ui.a === null
      ? `Tap your line of ${runLen} to take it.`
      : 'Now choose a torc to lift — you give it up to score.';
  }
  return ui.a === null ? 'Tap one of your torcs.' : 'Ride it to a marked point — it leaves a stud behind.';
}

/** Rebuild a claim ply from a chosen run key and a torc, for the keyboard path. */
export const claimPly = (runKey: number, ring: number): number => packClaim(Math.floor(runKey / 3), runKey % 3, ring);

export { claimAxis };
